import {normalizeResumeTranscript} from '../lib/sessionResume';
import {WritingResponseQueue} from '../lib/writingResponseQueue';
import {writingApi,type SessionWritingState} from '../lib/api';
import {reconcileResumeTranscript} from '../lib/resumeTranscriptReconciliation';
import {CandidateCompletionController,completionScope,CompletionScopeChangedError,type CompletionReceipt} from '../lib/candidateCompletion';
import {useAuth} from './AuthContext';
import {clearSessionTaskRecovery} from '../lib/sessionTaskRecoveryStorage';
import {applySessionDialogueDefinition,applySessionResponseDefinition} from '../lib/sessionDialogueDefinition';
import {applySessionMaterialDefinition} from '../lib/sessionMaterialDefinition';
import {applySessionModeDefinition,type SessionModeDefinition} from '../lib/sessionModeDefinition';
import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef,type SetStateAction } from 'react';
import {
  sessionsApi,
  getAccessToken,
  getActiveGroupId,
  auditApi,
  interviewsApi,
  llmConfigApi,
  ApiRequestError,
  type LLMStatusResponse,
  type SessionAccessCheckResult
} from '../lib/api';
import { getEffectiveBcp47Locale } from '../i18n/locale';
import {
  isCandidateAssignmentEligible,
  sortCandidateAssignments
} from '../components/candidate/candidateAssignmentUtils';
import { applySessionOralTiming,type SessionTimingSnapshot } from '../lib/partTimers';
import {applySessionAuthoredDefinition,type SessionAuthoredDefinition} from '../lib/sessionAuthoredDefinition';
import {applySessionContextDefinition,type SessionContextDefinition} from '../lib/sessionContextDefinition';
import {applySessionRetakeDefinition,type SessionRetakeDefinition} from '../lib/sessionRetakeDefinition';
import type { OralConversationStyle, TranscriptSpeechAct } from '../services/turnTakingPolicy';
import type { TranscriptEditFields } from '../services/answerReview';
import { clearScreenShareHandoff, requestEntireScreenShare } from '../services/secureScreenShareHandoff';

const NETWORK_CHECK_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/health`;
const NETWORK_LATENCY_THRESHOLD_MS = 4000;
const MIN_INTERVIEW_FETCH_RETRY_AFTER_MS = 5_000;
const DEFAULT_INTERVIEW_FETCH_RETRY_AFTER_MS = 60_000;

interface SystemCheck {
  camera: boolean;
  microphone: boolean;
  internet: boolean;
  browser: boolean;
  singleScreen: boolean;
  fullscreen: boolean;
  entireScreenShare: boolean;
}

interface InterviewDetails {
  id: string;
  title: string;
  description?: string;
  estimated_duration_minutes: number;
  part_b_duration_minutes?: number | null;
  focus_areas?: string[];
  difficulty_level: string;
  instructions?: string;
  scheduled_at?: string;
  assignment_scheduled_at?: string;
  available_from?: string | null;
  available_until?: string | null;
  assessment_mode?: 'hybrid' | 'part_a' | 'part_b';
  part_a_enabled?: boolean;
  part_b_enabled?: boolean;
  completion_deadline?: string;
  assignment_updated_at?: string;
  created_at?: string;
  updated_at?: string;
  questions?: any[];
  session_authored_definition?:SessionAuthoredDefinition;
  session_retake_scope?:SessionRetakeDefinition['retake_scope'];
  session_mode_definition?:SessionModeDefinition;
  material_definition_version?:number;
  dialogue_definition_version?:number;
  response_definition_version?:number;
  interview_type?: 'text' | 'voice' | 'mcq';
  allow_retake?: boolean;
  retake_scope?: 'none' | 'part_a' | 'part_b' | 'both' | string;
  max_attempts?: number;
  attempt_count?: number;
  assignment_status?: string;
  candidate_can_view_results?: boolean;
  group_id?: string;
  group_name?: string;
  group_type?: string;
  parent_group_id?: string | null;
  parent_group_name?: string | null;
  parent_group_type?: string | null;
  prompt_template?: string;
  /** Assessment language (en/hu/fr/de/es/ru) for AI speech and recognition. */
  language?: string;
  /** Spoken examiner identity for greetings. Not the Voice Studio voice label. */
  ai_interviewer_name?: string | null;
  placement_id?: string | null;
  proctoring_config?: Record<string, any>;
  assignment_proctoring_config?: Record<string, any>;
  /** Part B conversation tuning; null/absent means built-in defaults. */
  oral_conversation_style?: Partial<OralConversationStyle> | null;
}

interface InterviewState {
  id?: string;
  sessionId?: string;
  status: 'not_started' | 'system_check' | 'in_progress' | 'completed';
  startTime?: Date;
  endTime?: Date;
  transcript: Array<{
    role: 'ai' | 'candidate';
    content: string;
    timestamp: Date;
    speech_act?: TranscriptSpeechAct;
    model_request_id?:string;
    original_transcript?: string;
    edited?: boolean;
    edit_source?: 'candidate_review';
    edited_at?: string;
  }>;
  suspicionScore: number;
  violations: Array<{ type: string; severity: string; timestamp: Date }>;
  currentQuestionIndex: number;
  // Combined MCQ (Part A) + oral (Part B) assessment: which part of the
  // sequential flow this session is currently in.
  assessmentStage?: 'part_a' | 'part_b' | 'completed';
}

interface CandidateContextType {
  systemChecks: SystemCheck;
  interviewState: InterviewState;
  interviewDetails: InterviewDetails | null;
  assignedInterviews: InterviewDetails[];
  selectedInterviewId: string | null;
  loading: boolean;
  fetchAssignedInterview: (preferredInterviewId?: string) => Promise<InterviewDetails | null>;
  selectInterview: (interviewId: string) => Promise<InterviewDetails | null>;
  performSystemCheck: () => Promise<boolean>;
  ensureLlmAvailable: () => Promise<void>;
  checkInterviewAccess: (interviewId: string) => Promise<SessionAccessCheckResult>;
  startInterview: (interviewId: string, interviewType?: 'text' | 'voice' | 'mcq') => Promise<{
    assessmentStage: 'part_a' | 'part_b' | 'completed';
    sessionId?: string;
    resumed?: boolean;
    timingSnapshot?:SessionTimingSnapshot|null;
    oralDefinition?:SessionAuthoredDefinition|null;
    contextDefinition?:SessionContextDefinition|null;
    retakeDefinition?:SessionRetakeDefinition|null;
    modeDefinition?:SessionModeDefinition|null;
    materialDefinitionVersion?:number|null;
    dialogueDefinitionVersion?:number|null;
    responseDefinitionVersion?:number|null;
  }>;
  recordViolation: (violation: string, severity: 'low' | 'medium' | 'high') => void;
  recordIntegrityNote: (type: string, metadata?: Record<string, unknown>) => void;
  addTranscriptEntry: (
    role: 'ai' | 'candidate',
    content: string,
    speechAct?: TranscriptSpeechAct,
    edit?: TranscriptEditFields | null,
    modelRequestId?:string
  ) => void;
  submitWritingTask: (taskId: string, content: string) => Promise<void>;
  prepareWritingQueue:(sessionId:string,state:SessionWritingState)=>WritingResponseQueue;
  adoptResumedTranscript: (sessionId:string,entries:unknown)=>void;
  completeInterview: (options?:{readOnly?:boolean}) => Promise<CompletionReceipt>;
  resetInterview: () => void;
  requestRetake: () => Promise<boolean>;
}

const CandidateContext = createContext<CandidateContextType | undefined>(undefined);

interface CandidateProviderProps {
  children: ReactNode;
}

export function CandidateProvider({ children }: CandidateProviderProps) {
  const {user:completionUser,activeGroupId:completionGroup}=useAuth();
  const completionAuth=useRef({id:completionUser?.id,group:completionGroup});
  const writingQueue=useRef<{sessionId:string;owner:string|undefined;group:string|null;queue:WritingResponseQueue}|null>(null);
  completionAuth.current={id:completionUser?.id,group:completionGroup};
  const [systemChecks, setSystemChecks] = useState<SystemCheck>({
    camera: false,
    microphone: false,
    internet: false,
    browser: false,
    singleScreen: false,
    fullscreen: false,
    entireScreenShare: false,
  });

  const [interviewState, setRenderedInterviewState] = useState<InterviewState>({
    status: 'not_started',
    transcript: [],
    suspicionScore: 0,
    violations: [],
    currentQuestionIndex: 0,
  });

  // Context event updates own one current snapshot synchronously. A final answer
  // and completion may occur before React commits the next render; replaying a
  // React updater must also never repeat its existing best-effort write effects.
  const completionStateRef=useRef(interviewState);
  const transcriptOwnerRef=useRef<{sessionId?:string;id?:string;group:string|null}|null>(null);
  const setInterviewState=useCallback((update:SetStateAction<InterviewState>)=>{
    const next=typeof update==='function'?update(completionStateRef.current):update;
    completionStateRef.current=next;
    setRenderedInterviewState(next);
  },[]);
  const completionController=useRef<CandidateCompletionController|null>(null);
  if(!completionController.current)completionController.current=new CandidateCompletionController({
    scope:()=>{
      const key=completionScope(getAccessToken(),getActiveGroupId(),completionStateRef.current.sessionId);
      if(!key||!completionAuth.current.id)return null;
      const [id,group]=JSON.parse(key);
      return id===completionAuth.current.id&&(!completionAuth.current.group||group===completionAuth.current.group)?key:null;
    },
    complete:(id,payload,signal)=>sessionsApi.complete(id,payload,{signal}),
    status:(id,signal)=>sessionsApi.completionStatus(id,{signal})
  });
  const publishedCompletion=useRef<CompletionReceipt|null>(null);
  useEffect(()=>{completionController.current?.invalidate();publishedCompletion.current=null;writingQueue.current=null;},[completionUser?.id,completionGroup]);
  useEffect(()=>()=>completionController.current?.invalidate(),[]);
  const [interviewDetails, setInterviewDetails] = useState<InterviewDetails | null>(null);
  const [assignedInterviews, setAssignedInterviews] = useState<InterviewDetails[]>([]);
  const [selectedInterviewId, setSelectedInterviewId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const selectedInterviewIdRef = useRef<string | null>(null);
  const latestInterviewDetailsRef = useRef<InterviewDetails | null>(null);
  const activeSessionTimingRef=useRef<{interviewId:string;snapshot:SessionTimingSnapshot|null;oralDefinition:SessionAuthoredDefinition|null;contextDefinition:SessionContextDefinition|null;retakeDefinition:SessionRetakeDefinition|null;modeDefinition:SessionModeDefinition|null;materialDefinitionVersion:number|null;dialogueDefinitionVersion:number|null;responseDefinitionVersion:number|null}|null>(null);
  const interviewFetchInFlightRef = useRef<Promise<InterviewDetails | null> | null>(null);
  const interviewFetchCooldownUntilRef = useRef(0);
  const interviewFetchLastWarnedAtRef = useRef(0);

  useEffect(() => {
    selectedInterviewIdRef.current = selectedInterviewId;
  }, [selectedInterviewId]);

  useEffect(() => {
    latestInterviewDetailsRef.current = interviewDetails;
  }, [interviewDetails]);

  const logSystemIssue = useCallback(async (details: Record<string, any>) => {
    try {
      await auditApi.log('SYSTEM_ISSUE', details);
    } catch (error) {
      console.error('Failed to log system issue:', error);
    }
  }, []);

  const mapAssignmentSummary = useCallback((item: any): InterviewDetails => {
    return {
      id: item.id,
      title: item.title,
      description: item.description,
      estimated_duration_minutes: item.estimated_duration_minutes || 30,
      part_b_duration_minutes: item.part_b_duration_minutes ?? null,
      focus_areas: item.focus_areas || [],
      difficulty_level: item.difficulty_level || 'intermediate',
      instructions: item.instructions,
      scheduled_at: item.assignment_scheduled_at || item.scheduled_at,
      assignment_scheduled_at: item.assignment_scheduled_at || item.scheduled_at,
      available_from: item.available_from ?? null,
      available_until: item.available_until ?? null,
      assessment_mode: item.assessment_mode === 'hybrid' || item.assessment_mode === 'part_a' || item.assessment_mode === 'part_b'
        ? item.assessment_mode
        : undefined,
      part_a_enabled: item.part_a_enabled,
      part_b_enabled: item.part_b_enabled,
      completion_deadline: item.assignment_completion_deadline,
      assignment_updated_at: item.assignment_updated_at,
      created_at: item.created_at,
      updated_at: item.updated_at,
      interview_type: item.interview_type || 'text',
      allow_retake: Boolean(item.allow_retake),
      retake_scope: item.retake_scope || (item.allow_retake ? 'both' : 'none'),
      max_attempts: item.max_attempts || 1,
      attempt_count: item.attempt_count || 0,
      assignment_status: item.assignment_status,
      candidate_can_view_results: item.candidate_can_view_results,
      group_id: item.group_id,
      group_name: item.group_name,
      group_type: item.group_type,
      parent_group_id: item.parent_group_id,
      parent_group_name: item.parent_group_name,
      parent_group_type: item.parent_group_type,
      prompt_template: item.prompt_template || undefined,
      language: item.language || 'hu',
      ai_interviewer_name: item.ai_interviewer_name ?? null,
      placement_id: item.placement_id || null,
      questions: []
    };
  }, []);

  const hydrateInterviewDetails = useCallback(async (summary: InterviewDetails): Promise<InterviewDetails> => {
    try {
      const detailsResponse = await interviewsApi.get(summary.id);

      const hydrated: InterviewDetails = {
        ...summary,
        title: detailsResponse.interview.title,
        description: detailsResponse.interview.description,
        estimated_duration_minutes: detailsResponse.interview.estimated_duration_minutes,
        part_b_duration_minutes: detailsResponse.interview.part_b_duration_minutes ?? summary.part_b_duration_minutes ?? null,
        focus_areas: detailsResponse.interview.focus_areas,
        difficulty_level: detailsResponse.interview.difficulty_level,
        instructions: detailsResponse.interview.instructions,
        prompt_template: summary.prompt_template || detailsResponse.interview.prompt_template || undefined,
        language: detailsResponse.interview.language || summary.language || 'hu',
        ai_interviewer_name: detailsResponse.interview.ai_interviewer_name ?? summary.ai_interviewer_name ?? null,
        placement_id: (detailsResponse.interview as { placement_id?: string | null }).placement_id || summary.placement_id || null,
        available_from: detailsResponse.interview.available_from ?? summary.available_from ?? null,
        available_until: detailsResponse.interview.available_until ?? summary.available_until ?? null,
        assessment_mode: detailsResponse.interview.assessment_mode ?? summary.assessment_mode,
        part_a_enabled: detailsResponse.interview.part_a_enabled ?? summary.part_a_enabled,
        part_b_enabled: detailsResponse.interview.part_b_enabled ?? summary.part_b_enabled,
        questions: detailsResponse.questions || []
      };

      const accepted=activeSessionTimingRef.current?.interviewId===hydrated.id
        ? applySessionResponseDefinition(applySessionDialogueDefinition(applySessionMaterialDefinition(applySessionModeDefinition(applySessionRetakeDefinition(applySessionContextDefinition(applySessionAuthoredDefinition(applySessionOralTiming(hydrated,activeSessionTimingRef.current.snapshot),activeSessionTimingRef.current.oralDefinition),activeSessionTimingRef.current.contextDefinition),activeSessionTimingRef.current.retakeDefinition),activeSessionTimingRef.current.modeDefinition),activeSessionTimingRef.current.materialDefinitionVersion),activeSessionTimingRef.current.dialogueDefinitionVersion),activeSessionTimingRef.current.responseDefinitionVersion) : hydrated;
      setInterviewDetails(accepted);
      return accepted;
    } catch (error) {
      console.warn('Unable to fetch full interview details, using assignment summary only:', error);
      const accepted=activeSessionTimingRef.current?.interviewId===summary.id
        ? applySessionResponseDefinition(applySessionDialogueDefinition(applySessionMaterialDefinition(applySessionModeDefinition(applySessionRetakeDefinition(applySessionContextDefinition(applySessionAuthoredDefinition(applySessionOralTiming(summary,activeSessionTimingRef.current.snapshot),activeSessionTimingRef.current.oralDefinition),activeSessionTimingRef.current.contextDefinition),activeSessionTimingRef.current.retakeDefinition),activeSessionTimingRef.current.modeDefinition),activeSessionTimingRef.current.materialDefinitionVersion),activeSessionTimingRef.current.dialogueDefinitionVersion),activeSessionTimingRef.current.responseDefinitionVersion) : summary;
      setInterviewDetails(accepted);
      return accepted;
    }
  }, []);

  // Fetch the candidate's assigned interviews and resolve selected interview details
  const fetchAssignedInterview = useCallback(async (preferredInterviewId?: string): Promise<InterviewDetails | null> => {
    const now = Date.now();

    if (interviewFetchInFlightRef.current) {
      return interviewFetchInFlightRef.current;
    }

    if (now < interviewFetchCooldownUntilRef.current) {
      return latestInterviewDetailsRef.current;
    }

    setLoading(true);

    const request = (async (): Promise<InterviewDetails | null> => {
      try {
        const response = await interviewsApi.list();

        const eligibleInterviews = sortCandidateAssignments(
          (response.interviews || []).filter((item: any) => isCandidateAssignmentEligible(item))
        );

        const summaries = eligibleInterviews.map(mapAssignmentSummary);
        setAssignedInterviews(summaries);

        if (summaries.length === 0) {
          setSelectedInterviewId(null);
          setInterviewDetails(null);
          return null;
        }

        const requestedInterviewId = preferredInterviewId || selectedInterviewIdRef.current;
        const selectedSummary = summaries.find((item) => item.id === requestedInterviewId) || summaries[0];

        setSelectedInterviewId(selectedSummary.id);
        return await hydrateInterviewDetails(selectedSummary);
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 429) {
          const retryAfterMs = Math.max(
            MIN_INTERVIEW_FETCH_RETRY_AFTER_MS,
            error.retryAfterMs || DEFAULT_INTERVIEW_FETCH_RETRY_AFTER_MS
          );

          interviewFetchCooldownUntilRef.current = Date.now() + retryAfterMs;

          const logNow = Date.now();
          if (logNow - interviewFetchLastWarnedAtRef.current > 5000) {
            console.warn('Assigned interview refresh rate limited. Backing off.', { retryAfterMs });
            interviewFetchLastWarnedAtRef.current = logNow;
          }

          return latestInterviewDetailsRef.current;
        }

        console.error('Error fetching assigned interviews:', error);
        setAssignedInterviews([]);
        setSelectedInterviewId(null);
        setInterviewDetails(null);
        return null;
      } finally {
        setLoading(false);
      }
    })();

    interviewFetchInFlightRef.current = request;

    try {
      return await request;
    } finally {
      if (interviewFetchInFlightRef.current === request) {
        interviewFetchInFlightRef.current = null;
      }
    }
  }, [hydrateInterviewDetails, mapAssignmentSummary]);

  const selectInterview = useCallback(async (interviewId: string): Promise<InterviewDetails | null> => {
    if (!interviewId) {
      return null;
    }

    if (interviewDetails?.id === interviewId) {
      return interviewDetails;
    }
    completionController.current?.invalidate();publishedCompletion.current=null;writingQueue.current=null;

    const summary = assignedInterviews.find((item) => item.id === interviewId);
    if (!summary) {
      return fetchAssignedInterview(interviewId);
    }

    setSelectedInterviewId(interviewId);

    try {
      setLoading(true);
      return await hydrateInterviewDetails(summary);
    } finally {
      setLoading(false);
    }
  }, [assignedInterviews, fetchAssignedInterview, hydrateInterviewDetails, interviewDetails]);

  const performSystemCheck = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    
    try {
      // Camera check
      const cameraCheck = await checkCamera();
      
      // Microphone check
      const microphoneCheck = await checkMicrophone();
      
      // Internet speed check
      const internetCheck = await checkInternetSpeed();
      
      // Browser compatibility
      const browserCheck = checkBrowserCompatibility();
      
      // Single screen check
      const singleScreenCheck = await checkScreenCount();

      // Entire-screen share for live supervision (remote + default path).
      // Stream is kept alive and handed to SecureSessionSetup / hard lock.
      clearScreenShareHandoff(true);
      const screenShareResult = await requestEntireScreenShare();
      const entireScreenShareCheck = screenShareResult.ok;
      
      const checks: SystemCheck = {
        camera: cameraCheck,
        microphone: microphoneCheck,
        internet: internetCheck,
        browser: browserCheck,
        singleScreen: singleScreenCheck,
        fullscreen: true, // Fullscreen is enforced at secure setup / interview start
        entireScreenShare: entireScreenShareCheck,
      };

      setSystemChecks(checks);
      
      const allPassed = cameraCheck
        && microphoneCheck
        && internetCheck
        && browserCheck
        && singleScreenCheck
        && entireScreenShareCheck;
      
      await auditApi.log('SYSTEM_CHECK_COMPLETED', {
        checks,
        allPassed,
        timestamp: new Date().toISOString()
      });

      return allPassed;
    } catch (error) {
      console.error('System check error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const checkCamera = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  };

  const checkMicrophone = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (error) {
      return false;
    }
  };

  const checkInternetSpeed = async (): Promise<boolean> => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      if (!navigator.onLine) {
        return false;
      }

      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 5000);

      const startTime = performance.now();
      const response = await fetch(NETWORK_CHECK_URL, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });
      const elapsed = performance.now() - startTime;

      return response.ok && elapsed <= NETWORK_LATENCY_THRESHOLD_MS;
    } catch (error) {
      return false;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  };

  const checkBrowserCompatibility = (): boolean => {
    const hasMediaSupport = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    if (!hasMediaSupport) {
      return false;
    }

    if (interviewDetails?.interview_type === 'voice') {
      const hasSpeechRecognition =
        'SpeechRecognition' in window ||
        'webkitSpeechRecognition' in window;
      const hasSpeechSynthesis = 'speechSynthesis' in window;
      return hasSpeechRecognition && hasSpeechSynthesis;
    }

    return true;
  };

  const checkScreenCount = async (): Promise<boolean> => {
    try {
      // @ts-ignore - screen API might not be fully typed
      if (window.screen && window.screen.isExtended !== undefined) {
        // @ts-ignore
        return !window.screen.isExtended;
      }
      return true; // Default to true if can't detect
    } catch (error) {
      return true;
    }
  };

  const ensureLlmAvailable = useCallback(async () => {
    if (interviewDetails?.interview_type === 'mcq') {
      return;
    }
    let status: LLMStatusResponse | null = null;

    try {
      status = await llmConfigApi.status();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'LLM status check failed';
      await logSystemIssue({
        issue_type: 'llm_status_check_failed',
        severity: 'high',
        source: 'candidate_portal',
        error: message,
        interview_id: interviewDetails?.id,
        assignment_status: interviewDetails?.assignment_status,
        attempt_count: interviewDetails?.attempt_count,
        max_attempts: interviewDetails?.max_attempts
      });
      throw new Error('AI interview service is temporarily unavailable. Please try again later.');
    }

    const isConnected = status.connected !== false;
    const modelAvailable = status.model_available !== false;

    if (!isConnected || !modelAvailable) {
      await logSystemIssue({
        issue_type: 'llm_unavailable',
        severity: 'high',
        source: 'candidate_portal',
        provider: status.provider,
        endpoint: status.endpoint,
        model: status.model,
        model_available: status.model_available,
        error: status.error,
        interview_id: interviewDetails?.id,
        assignment_status: interviewDetails?.assignment_status,
        attempt_count: interviewDetails?.attempt_count,
        max_attempts: interviewDetails?.max_attempts
      });
      throw new Error('AI interview service is temporarily unavailable. Please try again later.');
    }
  }, [interviewDetails, logSystemIssue]);

  const checkInterviewAccess = useCallback(async (interviewId: string): Promise<SessionAccessCheckResult> => {
    const response = await sessionsApi.preflight(interviewId);
    return response.access;
  }, []);

  const startInterview = useCallback(async (interviewId: string, interviewType?: 'text' | 'voice' | 'mcq') => {
    completionController.current?.invalidate();publishedCompletion.current=null;writingQueue.current=null;
    const requestingOwner={...completionAuth.current};
    try {
      const resolvedType = interviewType ?? interviewDetails?.interview_type;
      const isMcqOnly = resolvedType === 'mcq';
      if (!isMcqOnly) {
        await ensureLlmAvailable();
      }

      // Create interview session via API
      const browserInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: getEffectiveBcp47Locale(),
      };
      
      const technicalChecks = {
        camera: systemChecks.camera,
        microphone: systemChecks.microphone,
        internet: systemChecks.internet,
        browser: systemChecks.browser,
        singleScreen: systemChecks.singleScreen,
        entireScreenShare: systemChecks.entireScreenShare,
      };
      
      const result = await sessionsApi.start(interviewId, browserInfo, technicalChecks);
      if(requestingOwner.id!==completionAuth.current.id||requestingOwner.group!==completionAuth.current.group)throw new CompletionScopeChangedError();
      const timingSnapshot=result.session.timing_snapshot || null;
      applySessionOralTiming({},timingSnapshot);
      const oralDefinition=result.oral_definition || null;
      applySessionAuthoredDefinition({},oralDefinition);
      const contextDefinition=result.session.context_snapshot || null;
      applySessionContextDefinition({},contextDefinition);
      const retakeDefinition=result.session.retake_snapshot || null;
      applySessionRetakeDefinition({},retakeDefinition);
      const modeDefinition=result.session.mode_snapshot || null;
      applySessionModeDefinition({},modeDefinition);
      const materialDefinitionVersion=result.session.material_definition_version ?? null;
      applySessionMaterialDefinition({},materialDefinitionVersion);
      const responseDefinitionVersion=result.session.response_definition_version ?? null;
      applySessionResponseDefinition({},responseDefinitionVersion);
      const dialogueDefinitionVersion=result.session.dialogue_definition_version ?? null;
      applySessionDialogueDefinition({},dialogueDefinitionVersion);
      activeSessionTimingRef.current={interviewId,snapshot:timingSnapshot,oralDefinition,contextDefinition,retakeDefinition,modeDefinition,materialDefinitionVersion,dialogueDefinitionVersion,responseDefinitionVersion};
      setInterviewDetails(current=>current?.id===interviewId ? applySessionResponseDefinition(applySessionDialogueDefinition(applySessionMaterialDefinition(applySessionModeDefinition(applySessionRetakeDefinition(applySessionContextDefinition(applySessionAuthoredDefinition(applySessionOralTiming(current,timingSnapshot),oralDefinition),contextDefinition),retakeDefinition),modeDefinition),materialDefinitionVersion),dialogueDefinitionVersion),responseDefinitionVersion) : current);
      // Combined MCQ (Part A) + oral (Part B) assessment: the session record
      // (defaults to 'part_a', or 'part_b' if this interview has no MCQ
      // module) tells the candidate UI which part of the flow to render.
      const assessmentStage = result.session?.assessment_stage || 'part_a';
      const resumed = Boolean(result.resumed);
      const sessionId = result.session?.id;
      transcriptOwnerRef.current={sessionId,id:requestingOwner.id,group:requestingOwner.group};

      let restoredTranscript: InterviewState['transcript'] = [];
      if (resumed && sessionId) {
        try {
          const sessionResult = await sessionsApi.get(sessionId);
          const rows = Array.isArray(sessionResult.session?.transcript)
            ? sessionResult.session.transcript
            : [];
          restoredTranscript = normalizeResumeTranscript(rows);
        } catch (hydrateError) {
          console.warn('Resumed session transcript hydrate skipped:', hydrateError);
        }
      }

      if(requestingOwner.id!==completionAuth.current.id||requestingOwner.group!==completionAuth.current.group)throw new CompletionScopeChangedError();
      setInterviewState(prev => ({
        ...prev,
        id: interviewId,
        sessionId,
        status: 'in_progress',
        startTime: prev.startTime && resumed ? prev.startTime : new Date(),
        currentQuestionIndex: resumed ? prev.currentQuestionIndex : 0,
        assessmentStage,
        transcript: restoredTranscript.length > 0 ? restoredTranscript : (resumed ? prev.transcript : [])
      }));

      try {
        await auditApi.log(resumed ? 'INTERVIEW_RESUMED' : 'INTERVIEW_STARTED', {
          interview_id: interviewId,
          session_id: sessionId,
          timestamp: new Date().toISOString()
        });
      } catch (auditError) {
        console.warn('Interview started but audit logging failed:', auditError);
      }

      return { assessmentStage, sessionId, resumed,timingSnapshot,oralDefinition,contextDefinition,retakeDefinition,modeDefinition,materialDefinitionVersion,dialogueDefinitionVersion,responseDefinitionVersion };
    } catch (error) {
      console.error('Error starting interview:', error);
      throw error;
    }
  }, [ensureLlmAvailable, interviewDetails?.interview_type, systemChecks,setInterviewState]);

  const recordViolation = useCallback((violation: string, severity: 'low' | 'medium' | 'high') => {
    setInterviewState(prev => {
      const scoreIncrease = severity === 'low' ? 10 : severity === 'medium' ? 25 : 50;
      const nextViolations = [...prev.violations, { type: violation, severity, timestamp: new Date() }];
      const nextScore = Math.min(prev.suspicionScore + scoreIncrease, 100);
      
      // Log asynchronously
      auditApi.log('VIOLATION_DETECTED', {
        violation,
        severity,
        previous_score: prev.suspicionScore,
        new_score: nextScore,
        timestamp: new Date().toISOString()
      }).catch(console.error);

      if (prev.sessionId) {
        sessionsApi.update(prev.sessionId, {
          integrity_events: nextViolations,
          integrity_score: nextScore
        }).catch(console.error);
      }

      return {
        ...prev,
        violations: nextViolations,
        suspicionScore: nextScore,
      };
    });
  }, [setInterviewState]);

  const recordIntegrityNote = useCallback((type: string, metadata?: Record<string, unknown>) => {
    setInterviewState(prev => {
      const nextViolations = [...prev.violations, {
        type,
        severity: 'low',
        timestamp: new Date(),
        informational: true,
        ...(metadata ? { metadata } : {})
      }];

      auditApi.log('ANSWER_EDITED_BEFORE_SUBMIT', {
        type,
        metadata: metadata || {},
        timestamp: new Date().toISOString()
      }).catch(console.error);

      if (prev.sessionId) {
        sessionsApi.update(prev.sessionId, {
          integrity_events: nextViolations
        }).catch(console.error);
      }

      return {
        ...prev,
        violations: nextViolations
      };
    });
  }, [setInterviewState]);

  const addTranscriptEntry = useCallback((
    role: 'ai' | 'candidate',
    content: string,
    speechAct?: TranscriptSpeechAct,
    edit?: TranscriptEditFields | null,
    modelRequestId?:string
  ) => {
    if(completionController.current?.isLocked(completionStateRef.current.sessionId))return;
    setInterviewState(prev => {
      // Omit speech_act entirely for plain questions/answers so the persisted
      // shape stays byte-identical to what pre-existing sessions produced.
      const entry = {
        role,
        content,
        timestamp: new Date(),
        ...(speechAct && speechAct !== 'question' && speechAct !== 'answer' ? { speech_act: speechAct } : {}),
        ...(edit ? edit : {}),
        ...(role==='ai' && modelRequestId?{model_request_id:modelRequestId}:{})
      };
      const nextTranscript = [...prev.transcript, entry];
      if (prev.sessionId) {
        sessionsApi.update(prev.sessionId, { transcript: nextTranscript }).catch(console.error);
      }
      return {
        ...prev,
        transcript: nextTranscript
      };
    });
  }, [setInterviewState]);

  const prepareWritingQueue=useCallback((sessionId:string,state:SessionWritingState)=>{
    const current=completionStateRef.current,owner={...completionAuth.current};
    if(current.sessionId!==sessionId||!owner.id||transcriptOwnerRef.current?.sessionId!==sessionId||transcriptOwnerRef.current.id!==owner.id||transcriptOwnerRef.current.group!==owner.group||!state.editable||completionController.current?.isLocked(sessionId))throw new CompletionScopeChangedError();
    const existing=writingQueue.current;
    if(existing?.sessionId===sessionId&&existing.owner===owner.id&&existing.group===owner.group)return existing.queue;
    const queue:WritingResponseQueue=new WritingResponseQueue(state,(taskId,body)=>writingApi.save(sessionId,taskId,body),():boolean=>
      writingQueue.current?.queue===queue&&completionStateRef.current.sessionId===sessionId&&completionAuth.current.id===owner.id&&completionAuth.current.group===owner.group
      && completionStateRef.current.status!=='completed'&&!completionController.current?.isLocked(sessionId));
    writingQueue.current={sessionId,owner:owner.id,group:owner.group,queue};return queue;
  },[]);

  const submitWritingTask=useCallback(async(taskId:string,content:string)=>{
    const current=writingQueue.current;
    if(!current||current.sessionId!==completionStateRef.current.sessionId||current.owner!==completionAuth.current.id||current.group!==completionAuth.current.group)throw new CompletionScopeChangedError();
    if(!await current.queue.save(taskId,content,'submitted'))throw new Error('Writing submission has not been acknowledged.');
    await current.queue.flush();
  },[]);

  const adoptResumedTranscript=useCallback((sessionId:string,entries:unknown)=>{
    if(sessionId!==completionStateRef.current.sessionId||completionController.current?.isLocked(sessionId))throw new CompletionScopeChangedError();
    const owner=transcriptOwnerRef.current;
    if(!owner?.id||owner.sessionId!==sessionId||owner.id!==completionAuth.current.id||owner.group!==completionAuth.current.group)throw new CompletionScopeChangedError();
    const validated=reconcileResumeTranscript([],entries,{version:1,start:0,total:Array.isArray(entries)?entries.length:-1});
    const incoming=normalizeResumeTranscript(validated),current=completionStateRef.current.transcript;
    // Hydration must not erase a same-tab answer created while its GET was in flight.
    if(current.length)reconcileResumeTranscript(current,incoming,{version:1,start:0,total:incoming.length});
    setInterviewState(previous=>({...previous,transcript:incoming}));
  },[setInterviewState]);

  const completeInterview = useCallback(async (options?:{readOnly?:boolean}):Promise<CompletionReceipt> => {
    let current=completionStateRef.current;const owner={...completionAuth.current};
    if(!current.sessionId)throw new CompletionScopeChangedError();
    const acceptedOwner=transcriptOwnerRef.current;
    if(!acceptedOwner||acceptedOwner.sessionId!==current.sessionId||acceptedOwner.id!==owner.id||acceptedOwner.group!==owner.group)throw new CompletionScopeChangedError();
    const sessionId=current.sessionId;
    const writing=writingQueue.current;
    if(!options?.readOnly&&!completionController.current?.isLocked(current.sessionId)&&writing?.sessionId===current.sessionId){
      await writing.queue.flush();
      if(completionStateRef.current.sessionId!==current.sessionId||completionAuth.current.id!==owner.id||completionAuth.current.group!==owner.group)throw new CompletionScopeChangedError();
      current=completionStateRef.current;
    }
    const receipt=await completionController.current!.confirm(sessionId,options?.readOnly?{}:{
      integrity_score:current.suspicionScore,transcript:current.transcript,
      integrity_events:current.violations.map(v=>({type:v.type,severity:v.severity,timestamp:v.timestamp.toISOString(),description:v.type}))
    },options?.readOnly===true);
    if(!receipt.isCurrent())throw new CompletionScopeChangedError();
    if(publishedCompletion.current!==receipt){
      publishedCompletion.current=receipt;
      clearSessionTaskRecovery(current.sessionId);
      // Publish only while this owner/session still holds confirmed authority.
      auditApi.log('INTERVIEW_COMPLETED',{interview_id:current.id,session_id:current.sessionId,
        duration:current.startTime?Date.now()-current.startTime.getTime():0,
        final_suspicion_score:current.suspicionScore,total_violations:current.violations.length,timestamp:new Date().toISOString()
      }).catch(()=>{});
      setInterviewState(previous=>previous.sessionId===current.sessionId&&receipt.isCurrent()?{...previous,status:'completed',endTime:new Date()}:previous);
    }
    return receipt;
  }, [setInterviewState]);

  const resetInterview = useCallback(() => {
    completionController.current?.invalidate();publishedCompletion.current=null;writingQueue.current=null;
    activeSessionTimingRef.current=null;
    setInterviewState({
      status: 'not_started',
      transcript: [],
      suspicionScore: 0,
      violations: [],
      currentQuestionIndex: 0,
    });
    setSelectedInterviewId(null);
    setAssignedInterviews([]);
    setInterviewDetails(null);
    setSystemChecks({
      camera: false,
      microphone: false,
      internet: false,
      browser: false,
      singleScreen: false,
      fullscreen: false,
      entireScreenShare: false,
    });
  }, [setInterviewState]);

  const requestRetake = useCallback(async (): Promise<boolean> => {
    if (!interviewDetails?.id) return false;
    try {
      completionController.current?.invalidate();publishedCompletion.current=null;writingQueue.current=null;
      await sessionsApi.retake(interviewDetails.id);
      activeSessionTimingRef.current=null;
      // Reset local state so candidate can go through system check again
      setInterviewState({
        status: 'not_started',
        transcript: [],
        suspicionScore: 0,
        violations: [],
        currentQuestionIndex: 0,
      });
      setSystemChecks({
        camera: false,
        microphone: false,
        internet: false,
        browser: false,
        singleScreen: false,
        fullscreen: false,
        entireScreenShare: false,
      });
      // Re-fetch interview details to get updated attempt_count
      await fetchAssignedInterview(interviewDetails.id);
      return true;
    } catch (error) {
      console.error('Error requesting retake:', error);
      return false;
    }
  }, [interviewDetails, fetchAssignedInterview,setInterviewState]);

  const value: CandidateContextType = {
    systemChecks,
    interviewState,
    interviewDetails,
    assignedInterviews,
    selectedInterviewId,
    loading,
    fetchAssignedInterview,
    selectInterview,
    performSystemCheck,
    ensureLlmAvailable,
    checkInterviewAccess,
    startInterview,
    recordViolation,
    recordIntegrityNote,
    addTranscriptEntry,
    submitWritingTask,
    prepareWritingQueue,
    completeInterview,
    adoptResumedTranscript,
    resetInterview,
    requestRetake,
  };

  return (
    <CandidateContext.Provider value={value}>
      {children}
    </CandidateContext.Provider>
  );
}

export function useCandidate() {
  const context = useContext(CandidateContext);
  if (context === undefined) {
    throw new Error('useCandidate must be used within a CandidateProvider');
  }
  return context;
}
